import { Box, FormControl, FormHelperText, Stack } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import type { TimeWindow } from '@provision/sidekick-utils';
import dayjs, { Dayjs } from 'dayjs';
import { useId } from 'react';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import { MINUTE_STEPS, TIME_FORMAT } from 'src/components/WeekSchedule/const';
import {
  describeScheduleWindows,
  expandScheduleDays,
  SCHEDULE_WEEKDAY_OPTIONS,
  summarizeScheduleDays,
} from './schedule-windows';

const WEEKDAY_MENU_OPTIONS: SelectMenuOption[] = SCHEDULE_WEEKDAY_OPTIONS.map((option) => ({
  value: String(option.value),
  title: option.label,
}));

/** Any fixed day: the pickers need a full date, but only its time is read back. */
const CLOCK_REFERENCE_DATE = '2024-01-01';

export interface ScheduleConditionEditorProps {
  windows: TimeWindow[];
  onChange: (windows: TimeWindow[]) => void;
  /** the query builder's `rule-value` class, which places the editor in the row's value cell */
  className?: string;
  /**
   * marks a schedule the form will refuse to save: no day picked, or an end time not after the
   * start time
   */
  markInvalid?: boolean;
}

/**
 * The Custom schedule row: "[days] from [time] to [time]". One time range per condition — another
 * range is another Custom schedule condition, which the builder already adds and removes.
 */
export const ScheduleConditionEditor = ({
  windows,
  onChange,
  className,
  markInvalid = false,
}: ScheduleConditionEditorProps) => {
  const fromFieldId = useId();
  const toFieldId = useId();
  const { weekDays, timeFrom, timeTo } = describeScheduleWindows(windows);
  const selectedDays = SCHEDULE_WEEKDAY_OPTIONS.filter((option) =>
    weekDays.includes(option.value),
  );
  const isMissingDays = markInvalid && selectedDays.length === 0;
  const from = toClock(timeFrom);
  const to = toClock(timeTo);
  // the pickers only limit what can be picked; a typed time still arrives through `onChange`
  const isInvertedRange = markInvalid && !to.isAfter(from);

  const emit = (nextWeekDays: readonly number[], nextFrom: string, nextTo: string): void =>
    onChange(expandScheduleDays(nextWeekDays, nextFrom, nextTo));

  const emitTime = (field: 'from' | 'to') => (value: Dayjs | null) => {
    if (!value?.isValid()) {
      return;
    }
    const time = value.format(TIME_FORMAT);
    emit(weekDays, field === 'from' ? time : timeFrom, field === 'to' ? time : timeTo);
  };

  return (
    <Stack className={className} spacing={1}>
      <FormControl error={isMissingDays}>
        <SelectMenu
          label="Days"
          variant="field"
          size="small"
          menuWidth="trigger"
          multiple
          options={WEEKDAY_MENU_OPTIONS}
          values={selectedDays.map((option) => String(option.value))}
          placeholder="Days"
          // the trigger stays one line: named days up to two, then a count
          triggerLabel={
            selectedDays.length
              ? summarizeScheduleDays(selectedDays.map((option) => option.value))
              : undefined
          }
          error={isMissingDays}
          onValuesChange={(values) =>
            emit(
              SCHEDULE_WEEKDAY_OPTIONS.filter((option) =>
                values.includes(String(option.value)),
              ).map((option) => option.value),
              timeFrom,
              timeTo,
            )
          }
        />
        {isMissingDays ? <FormHelperText>Select at least one day</FormHelperText> : null}
      </FormControl>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {/* the boxes let the pickers shrink to share the value cell instead of wrapping */}
        <Box sx={{ flex: '1 1 7.5rem', minWidth: '7.5rem' }}>
          <FormFieldLabel htmlFor={fromFieldId}>From</FormFieldLabel>
          <TimePicker
            value={from}
            maxTime={to.subtract(MINUTE_STEPS, 'minute')}
            timeSteps={{ minutes: MINUTE_STEPS }}
            onChange={emitTime('from')}
            slotProps={{ textField: { id: fromFieldId, size: 'small', fullWidth: true } }}
          />
        </Box>
        <Box sx={{ flex: '1 1 7.5rem', minWidth: '7.5rem' }}>
          <FormFieldLabel htmlFor={toFieldId}>To</FormFieldLabel>
          <TimePicker
            value={to}
            minTime={from.add(MINUTE_STEPS, 'minute')}
            timeSteps={{ minutes: MINUTE_STEPS }}
            onChange={emitTime('to')}
            slotProps={{
              textField: {
                id: toFieldId,
                size: 'small',
                fullWidth: true,
                error: isInvertedRange,
                helperText: isInvertedRange ? 'Must be after From' : undefined,
              },
            }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

function toClock(time: string): Dayjs {
  return dayjs(`${CLOCK_REFERENCE_DATE}T${time}`);
}
